import React, { useEffect, useState } from 'react';
import { Button } from '../UI/Button';
import { useToast } from '../UI/ToastProvider';
import { agentBotService, AgentBotSignature } from '../../services/agentBotService';
import { apiErrorMessage } from '../../utils/apiErrorMessage';

interface AgentBotSignatureSectionProps {
  botId: string;
}

export const AgentBotSignatureSection: React.FC<AgentBotSignatureSectionProps> = ({ botId }) => {
  const { addToast } = useToast();
  const [signature, setSignature] = useState<AgentBotSignature | null>(null);
  const [messageSignature, setMessageSignature] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadSignature = async () => {
      setLoading(true);
      try
      {
        const data = await agentBotService.getSignature(botId);
        setSignature(data);
        setMessageSignature(data.messageSignature);
        setImage(null);
        setRemoveImage(false);
      }
      catch (error)
      {
        addToast(apiErrorMessage(error, 'Erro ao carregar assinatura do robô'), 'error');
      }
      finally
      {
        setLoading(false);
      }
    };

    void loadSignature();
  }, [addToast, botId]);

  useEffect(() => {
    if (!image)
    {
      setPreviewUrl(removeImage ? null : (signature?.imageUrl ?? null));
      return;
    }

    const objectUrl = URL.createObjectURL(image);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [image, removeImage, signature?.imageUrl]);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0] ?? null;
    setImage(selected);
    setRemoveImage(false);
  };

  const handleRemoveImage = () => {
    setImage(null);
    setPreviewUrl(null);
    setRemoveImage(true);
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);

    try
    {
      const updated = await agentBotService.updateSignature(
        botId,
        messageSignature,
        image,
        removeImage,
      );
      setSignature(updated);
      setMessageSignature(updated.messageSignature);
      setImage(null);
      setRemoveImage(false);
      addToast('Assinatura do robô salva com sucesso', 'success');
    }
    catch (error)
    {
      addToast(apiErrorMessage(error, 'Erro ao salvar assinatura do robô'), 'error');
    }
    finally
    {
      setSaving(false);
    }
  };

  if (loading)
  {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Carregando assinatura...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSave} className="space-y-6">
      <section className="bg-white border border-gray-200 rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900">Assinatura de e-mail</h2>
        <p className="text-sm text-gray-600 mt-1 mb-4">
          Texto e imagem anexados ao final das respostas de e-mail enviadas por este robô.
        </p>
        <label htmlFor="bot-signature" className="block text-sm font-medium text-gray-700 mb-2">
          Texto da assinatura
        </label>
        <textarea
          id="bot-signature"
          rows={8}
          value={messageSignature}
          onChange={(event) => setMessageSignature(event.target.value)}
          placeholder={'Atenciosamente,\nEquipe de Atendimento\nBaruk Know'}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-900 focus:border-blue-900"
        />
        <p className="text-xs text-gray-500 mt-2">
          A assinatura é adicionada automaticamente nas respostas de e-mail do robô.
        </p>
      </section>

      <section className="bg-white border border-gray-200 rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900">Imagem do rodapé</h2>
        <p className="text-sm text-gray-600 mt-1 mb-4">
          A imagem será enviada incorporada ao e-mail, sem depender de uma URL pública.
        </p>
        <input
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          onChange={handleImageChange}
          className="block w-full text-sm text-gray-600"
        />
        {previewUrl && (
          <div className="mt-4 flex items-start gap-4">
            <img src={previewUrl} alt="Prévia da assinatura" className="max-h-24 max-w-full object-contain" />
            <Button type="button" variant="dangerOutline" size="sm" onClick={handleRemoveImage}>
              Remover imagem
            </Button>
          </div>
        )}
      </section>

      <Button type="submit" disabled={saving}>
        {saving ? 'Salvando...' : 'Salvar assinatura'}
      </Button>
    </form>
  );
};
